import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import StripePaymentForm from '../../components/billing/StripePaymentForm';
import OrderSummary from '../../components/billing/OrderSummary';
import RedeemGiftCardForm from '../../components/billing/RedeemGiftCardForm';
import { getStripePromise } from '../../lib/stripePromise';
import { formatMoney } from '../../lib/formatMoney';

const API = process.env.REACT_APP_API_URL;

const CheckoutPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [prices, setPrices] = useState(null);
    const [sub, setSub] = useState(null);          // last successful create_subscription response
    const [appliedCode, setAppliedCode] = useState(null);
    const [pageError, setPageError] = useState(null);
    const [couponOpen, setCouponOpen] = useState(Boolean(searchParams.get('code')));
    const [couponInput, setCouponInput] = useState(searchParams.get('code') || '');
    const [couponError, setCouponError] = useState(null);
    const [couponLoading, setCouponLoading] = useState(false);
    const [paying, setPaying] = useState(false);
    const [preview, setPreview] = useState(null);      // {credit_balance_cents, price}
    const [awaitingConfirm, setAwaitingConfirm] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [giftOpen, setGiftOpen] = useState(false);
    const reqSeqRef = useRef(0);

    const createSubscription = useCallback(async (code) => {
        const res = await fetch(`${API}/api/create_subscription.php`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ plan: 'monthly', ...(code ? { code } : {}) }),
        });
        let data = null;
        try { data = await res.json(); } catch (_) { /* non-JSON body */ }
        return { res, data };
    }, []);

    const handleSubResponse = useCallback((data, code) => {
        if (data.type === 'none') { navigate('/upgrade/success'); return; }
        setSub(data);
        setAppliedCode(code || null);
    }, [navigate]);

    // Creating the subscription finalizes the first invoice, which is when
    // Stripe applies any customer balance — so this must not run on mount for
    // users who have credit. They confirm first (startCheckout).
    const startCheckout = useCallback(async (initialCode) => {
        const seq = ++reqSeqRef.current;
        try {
            const { res, data } = await createSubscription(initialCode || undefined);
            if (seq !== reqSeqRef.current) return;
            if (res.status === 409) { navigate('/settings/profile'); return; }
            if (!res.ok || !data || data.status !== 'success') {
                if (initialCode) {
                    const retry = await createSubscription(undefined);
                    if (seq === reqSeqRef.current && retry.res.ok && retry.data?.status === 'success') {
                        setCouponError(data?.message || 'Coupon could not be applied.');
                        setAwaitingConfirm(false);
                        handleSubResponse(retry.data, null);
                        return;
                    }
                }
                setPageError(data?.message || 'Could not start checkout. Please try again.');
                return;
            }
            setAwaitingConfirm(false);
            handleSubResponse(data, initialCode || null);
        } catch (err) {
            setPageError('Could not start checkout. Please try again.');
        }
    }, [createSubscription, handleSubResponse, navigate]);

    // Initial load: prices + subscription (with any code passed from pricing page).
    useEffect(() => {
        // No publishable key: the render shows the config-error card; skip the
        // server call so misconfigured deploys don't mint orphan subscriptions.
        // (React StrictMode double-mounts in dev re-run this effect; the server
        // prunes incomplete subs and resumes an existing card-less trial, so
        // double-creates stay harmless.)
        if (!process.env.REACT_APP_STRIPE_PK) return;

        let cancelled = false;
        (async () => {
            try {
                const priceRes = await fetch(`${API}/api/get_prices.php`, { credentials: 'include' });
                const priceData = await priceRes.json();
                if (!cancelled && priceData.status === 'success') setPrices(priceData);

                const prevRes = await fetch(`${API}/api/get_checkout_preview.php`, { credentials: 'include' });
                const prevData = await prevRes.json();
                if (cancelled) return;
                // Fail CLOSED: a preview we could not read is not proof of a
                // zero balance, and guessing wrong spends the user's credit
                // without a click. Surface the error instead.
                if (!prevRes.ok || prevData.status !== 'success') {
                    setPageError('Could not load checkout details. Please try again.');
                    return;
                }
                setPreview(prevData);

                if ((prevData.credit_balance_cents || 0) > 0) {
                    // Hold: creating the subscription now would spend this
                    // balance without the user ever clicking anything.
                    setAwaitingConfirm(true);
                    return;
                }

                await startCheckout(searchParams.get('code') || '');
            } catch (err) {
                if (!cancelled) setPageError('Could not start checkout. Please try again.');
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const applyCoupon = async () => {
        const code = couponInput.trim();
        if (!code) return;
        setCouponLoading(true);
        setCouponError(null);
        const seq = ++reqSeqRef.current;
        try {
            const { res, data } = await createSubscription(code);
            if (seq !== reqSeqRef.current) return;
            if (res.status === 409) { navigate('/settings/profile'); return; }
            if (!res.ok || !data || data.status !== 'success') {
                setCouponError(data?.message || 'Coupon could not be applied.');
                return; // keep the existing subscription + secret
            }
            handleSubResponse(data, code);
        } catch (err) {
            setCouponError('Network error. Please try again.');
        } finally {
            setCouponLoading(false);
        }
    };

    const removeCoupon = async () => {
        setCouponLoading(true);
        setCouponError(null);
        const seq = ++reqSeqRef.current;
        try {
            const { res, data } = await createSubscription(undefined);
            if (seq !== reqSeqRef.current) return;
            if (res.status === 409) { navigate('/settings/profile'); return; }
            if (!res.ok || !data || data.status !== 'success') {
                setCouponError(data?.message || 'Could not remove the coupon.');
                return;
            }
            setCouponInput('');
            handleSubResponse(data, null);
        } catch (err) {
            setCouponError('Network error. Please try again.');
        } finally {
            setCouponLoading(false);
        }
    };

    const elementsOptions = useMemo(() => sub && ({
        clientSecret: sub.client_secret,
        appearance: {
            theme: 'night',
            variables: {
                colorPrimary: '#06b6d4',
                colorBackground: '#0b0e14',
                colorText: '#e5e5e5',
                borderRadius: '8px',
            },
        },
    }), [sub]);

    if (!process.env.REACT_APP_STRIPE_PK) {
        return (
            <div className="p-8 md:p-12 text-white text-center">
                <div className="max-w-md mx-auto bg-[#0b0e14]/60 border border-white/10 rounded-xl p-6">
                    <h1 className="text-xl font-bold mb-2">Checkout unavailable</h1>
                    <p className="text-neutral-400 text-sm">Payments are not configured. Please contact support.</p>
                </div>
            </div>
        );
    }

    if (pageError) {
        return (
            <div className="p-8 md:p-12 text-white text-center">
                <div className="max-w-md mx-auto bg-[#0b0e14]/60 border border-red-900/40 rounded-xl p-6">
                    <h1 className="text-xl font-bold mb-2">Something went wrong</h1>
                    <p className="text-neutral-400 text-sm mb-4">{pageError}</p>
                    <button onClick={() => window.location.reload()} className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold py-2 px-6 rounded-lg">
                        Try again
                    </button>
                </div>
            </div>
        );
    }

    const monthlyPrice = prices?.monthly;
    const annualPrice = prices?.annual;
    const isTrial = sub?.type === 'setup';
    const trialStart = isTrial && sub.trial?.first_charge_at ? new Date(sub.trial.first_charge_at) : null;

    const creditBalance = preview?.credit_balance_cents || 0;
    const monthlyAmount = prices?.monthly?.amount ?? preview?.price?.amount ?? 0;
    const monthlyCurrency = prices?.monthly?.currency ?? preview?.price?.currency ?? 'usd';
    const confirmBreakdown = awaitingConfirm ? {
        subtotal: monthlyAmount,
        lines: [{ type: 'credit', label: 'Account credit', amount: -Math.min(creditBalance, monthlyAmount) }],
        tax: 0,
        total: monthlyAmount,
        due_today: Math.max(0, monthlyAmount - creditBalance),
    } : null;

    return (
        <div className="p-6 md:p-12 text-white">
            <div className="max-w-4xl mx-auto">
                <h1 className="text-2xl font-bold mb-8">Upgrade to Premium</h1>
                <div className="grid md:grid-cols-2 gap-8">
                    {/* Order column */}
                    <div className="space-y-4">
                        {/* Line item */}
                        <div className="rounded-xl border border-cyan-500/60 bg-[#0b0e14]/60 p-5">
                            <div className="flex justify-between items-baseline gap-4">
                                <div>
                                    <h2 className="font-semibold">Premium Monthly</h2>
                                    <p className="text-xs text-neutral-400 mt-0.5">Unlimited AI optimizations. Cancel anytime.</p>
                                </div>
                                <span className="text-sm text-neutral-300 whitespace-nowrap">
                                    1 × <span className="font-bold text-white">{monthlyPrice ? formatMoney(monthlyPrice.amount, monthlyPrice.currency) : '…'}</span> /mo
                                </span>
                            </div>
                        </div>
                        <p className="px-1 text-xs text-neutral-500">
                            Premium Annual{annualPrice ? ` (${formatMoney(annualPrice.amount, annualPrice.currency)}/yr)` : ''} — coming soon
                        </p>

                        {/* Order summary — server breakdown, with legacy fallback */}
                        {awaitingConfirm ? (
                            <OrderSummary breakdown={confirmBreakdown} currency={monthlyCurrency} trial={null} />
                        ) : sub?.breakdown ? (
                            <OrderSummary breakdown={sub.breakdown} currency={sub.currency} trial={isTrial ? sub.trial : null} />
                        ) : (
                            <div className="rounded-xl border border-white/10 bg-[#0b0e14]/60 p-5">
                                <div className="flex justify-between">
                                    <span className="text-neutral-400">Due today</span>
                                    <span className="text-xl font-bold">{sub ? formatMoney(sub.amount_due, sub.currency) : '…'}</span>
                                </div>
                                {isTrial && sub.trial && (
                                    <p className="text-xs text-neutral-400 mt-2">
                                        then {formatMoney(sub.trial.amount, sub.trial.currency)}/mo starting {trialStart ? trialStart.toLocaleDateString() : 'after your trial'}
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Collapsible coupon */}
                        {!isTrial && (
                            <div className="rounded-xl border border-white/10 bg-[#0b0e14]/60">
                                <button
                                    type="button"
                                    onClick={() => setCouponOpen((o) => !o)}
                                    className="flex w-full items-center justify-between p-4 text-sm font-semibold"
                                >
                                    Add a coupon code
                                    <ChevronDownIcon className={`h-4 w-4 text-neutral-400 transition-transform ${couponOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {couponOpen && (
                                    <div className="px-4 pb-4">
                                        {appliedCode ? (
                                            <div className="flex items-center justify-between rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2 text-sm">
                                                <span className="text-emerald-300">{appliedCode} applied</span>
                                                <button
                                                    onClick={removeCoupon}
                                                    disabled={couponLoading || paying}
                                                    className="text-xs text-neutral-400 underline hover:text-white disabled:opacity-50"
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="flex gap-2">
                                                <input
                                                    className="flex-1 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm placeholder-neutral-600 focus:ring-2 focus:ring-cyan-500/60 focus:outline-none"
                                                    placeholder="Coupon code"
                                                    value={couponInput}
                                                    onChange={(e) => setCouponInput(e.target.value)}
                                                />
                                                <button
                                                    onClick={applyCoupon}
                                                    disabled={couponLoading || !couponInput.trim() || !sub || paying}
                                                    className="px-4 py-2 text-sm bg-white/10 hover:bg-white/20 rounded-lg disabled:opacity-50"
                                                >
                                                    {couponLoading ? 'Checking…' : 'Apply'}
                                                </button>
                                            </div>
                                        )}
                                        {couponError && <p className="text-red-400 text-xs mt-2">{couponError}</p>}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Collapsible gift card */}
                        {!isTrial && (
                            <div className="rounded-xl border border-white/10 bg-[#0b0e14]/60">
                                <button
                                    type="button"
                                    onClick={() => setGiftOpen((o) => !o)}
                                    className="flex w-full items-center justify-between p-4 text-sm font-semibold"
                                >
                                    Add a gift card
                                    <ChevronDownIcon className={`h-4 w-4 text-neutral-400 transition-transform ${giftOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {giftOpen && (
                                    <div className="px-4 pb-4">
                                        <RedeemGiftCardForm onRedeemed={() => window.location.reload()} />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Payment */}
                    <div className="rounded-xl border border-white/10 bg-[#0b0e14]/60 p-5">
                        {awaitingConfirm ? (
                            <div>
                                <h2 className="font-semibold mb-2">Ready when you are</h2>
                                <p className="text-sm text-neutral-400 mb-5">
                                    You have {formatMoney(creditBalance, monthlyCurrency)} of account credit, and it will be
                                    applied to this subscription. Any coupon or free trial applies first, so you may spend
                                    less than that today.
                                </p>
                                <button
                                    onClick={() => { setConfirming(true); startCheckout(searchParams.get('code') || ''); }}
                                    disabled={confirming}
                                    className="w-full bg-cyan-500 hover:bg-cyan-400 text-black font-semibold py-3 rounded-lg disabled:opacity-50 transition-colors"
                                >
                                    {confirming ? 'Starting…' : 'Start Premium using your credit'}
                                </button>
                            </div>
                        ) : sub && elementsOptions ? (
                            <Elements key={sub.client_secret} stripe={getStripePromise()} options={elementsOptions}>
                                <StripePaymentForm
                                    payLabel={isTrial ? 'Start free trial' : `Pay ${formatMoney(sub.breakdown ? sub.breakdown.due_today : sub.amount_due, sub.currency)}`}
                                    mode={isTrial ? 'setup' : 'payment'}
                                    onSubmittingChange={setPaying}
                                />
                            </Elements>
                        ) : (
                            <div className="flex items-center justify-center h-40">
                                <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CheckoutPage;
